import React, { useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import type {
  AddressType,
  GooglePlacesAddress,
  PinData,
  Region,
} from '@sweetgreen/garnish';
import { formatGoogleAddress, useEffectOnce } from '@sweetgreen/garnish';

import type { AddAddressSuccess, Customer } from '@order/graphql';
import { DeliveryPreferenceType } from '@order/graphql';
import type { UseNavigateToMenuProps } from '@order/hooks';
import { useOrders } from '@order/Order';

import { useAddAddressMutation } from '../../../components/Address/GraphQL/Address.generated';
import type {
  LocationPredictionsData,
  LocationQueryType,
} from '../LocationsScreen.types';
import { DEFAULT_REGION } from '../LocationsScreen.utils';
import { useNavigateToMenuFromLocation } from './LocationsScreen.navigation';
import { useLocationsByAddress } from './LocationsScreen.search';
import { useDefaultDeliveryChannel } from './useDefaultDeliveryChannel';

export const useDelivery = ({
  customer,
  isFetchingCustomer,
  searchString,
  locationSearchType,
  onOpenDeliveryRestaurant,
  setLocationSearchType,
  setLocationQueryType,
  setSearchString,
  setRegion,
}: {
  readonly customer: Partial<Customer>;
  readonly isFetchingCustomer: boolean;
  readonly searchString: string;
  readonly locationSearchType: AddressType;
  readonly onOpenDeliveryRestaurant?: (props?: UseNavigateToMenuProps) => void;
  readonly setLocationSearchType: (type: AddressType) => void;
  readonly setLocationQueryType: (type: LocationQueryType) => void;
  readonly setSearchString: (query: string) => void;
  readonly setRegion: (region: Region) => void;
}) => {
  // ───────────────────────── Order History ────────────────────────────────
  const { orders, isLoadingOrders, fetchOrders } = useOrders();
  const isLoadingDeliveryLocation = isFetchingCustomer || isLoadingOrders;
  const isDelivery = locationSearchType === 'delivery';

  useFocusEffect(
    React.useCallback(() => {
      fetchOrders();
    }, [fetchOrders]),
  );

  // ────────────────────── Predictions ─────────────────────────────────────
  const [placeId, setPlaceId] = React.useState('');
  const [predictionsSearchString, setPredictionsSearchString] =
    React.useState(searchString);
  const [predictionsData, setPredictions] = React.useState(defaultPredictions);

  const setPredictionsData = React.useCallback(
    (data: LocationPredictionsData) => {
      const current = JSON.stringify(predictionsData);
      const next = JSON.stringify(data);
      const hasChanged = current !== next;

      if (hasChanged) setPredictions(data);
    },
    [predictionsData],
  );

  // ────────────────────── Location By Address ─────────────────────────────
  const {
    fetchingDelivery,
    foundLocation,
    noLocationFound,
    googlePlacesAddress,
    setGooglePlacesAddress,
    setLoadingAddress,
  } = useLocationsByAddress(placeId);

  // ───────────────────────────── Navigation ───────────────────────────────
  const navigateToMenu = useNavigateToMenuFromLocation();
  const handleOpenDeliveryRestaurant =
    onOpenDeliveryRestaurant ?? navigateToMenu;

  const [addAddressResponse, addAddress] = useAddAddressMutation();
  const isAddingAddress = addAddressResponse?.fetching ?? false;

  const openDeliveryMenu = useCallback(
    (existingAddressId: string, existingAddressName?: string) => {
      handleOpenDeliveryRestaurant({
        orderType: 'delivery',
        restaurantId: foundLocation?.restaurant?.id,
        restaurantSlug: foundLocation?.restaurant?.slug,
        restaurantName: foundLocation?.restaurant?.name,
        deliveryAddressName: existingAddressName,
        deliveryOrderDetail: {
          addressId: existingAddressId,
          tip: 0,
          deliveryFee: foundLocation?.restaurant?.deliveryFee ?? 0,
          vendor: foundLocation?.vendor?.name ?? '',
          vendorRestaurantId: foundLocation?.vendor?.restaurantId ?? '',
        },
      });
    },
    [
      foundLocation?.restaurant?.deliveryFee,
      foundLocation?.restaurant?.id,
      foundLocation?.restaurant?.name,
      foundLocation?.restaurant?.slug,
      foundLocation?.vendor?.name,
      foundLocation?.vendor?.restaurantId,
      handleOpenDeliveryRestaurant,
    ],
  );

  React.useEffect(() => {
    const addedAddressData = addAddressResponse.data
      ?.addAddress as AddAddressSuccess;

    const addressId = addedAddressData?.address?.id ?? '';
    const addressName = addedAddressData?.address?.name ?? '';

    if (!addressId) return;

    openDeliveryMenu(addressId, addressName);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addAddressResponse]);

  // ───────────────────── Change Delivery Region & Pin ──────────────────────
  const [deliveryPin, setDeliveryPin] = React.useState<PinData | null>(null);
  const changeDeliveryRegion = React.useCallback(
    (address: Partial<GooglePlacesAddress>) => {
      const addressName =
        (address as { readonly name: string })?.name ?? address?.street;
      const formattedAddress = formatGoogleAddress(address);
      const addresses = customer?.addresses ?? [];
      const customerAddress = addresses.find(
        ({ googlePlaceId }) => googlePlaceId === address?.googlePlaceId,
      );
      const isHomeOrWork = ['home', 'work'].includes(
        customerAddress?.name ?? '',
      );
      const customOrUndefined = customerAddress?.name ? 'custom' : undefined;
      const deliveryAddressType = (
        isHomeOrWork ? customerAddress?.name ?? 'home' : customOrUndefined
      ) as 'home' | 'work' | 'custom' | undefined;

      setSearchString(formattedAddress ?? '');
      setPlaceId(address.googlePlaceId ?? '');
      setRegion({
        ...DEFAULT_REGION,
        lat: address.latitude ?? 0,
        lng: address.longitude ?? 0,
      });
      setDeliveryPin((prev) => ({
        id: address.googlePlaceId ?? '',
        restaurantSlug: '',
        name:
          prev?.id === address.googlePlaceId
            ? prev?.name ?? addressName
            : addressName,
        address: formattedAddress,
        lat: address.latitude ?? 0,
        lng: address.longitude ?? 0,
        active: true,
        acceptingOrders: true,
        isDeliveryAddress: true,
        deliveryAddressType,
      }));
    },
    [customer?.addresses, setRegion, setSearchString],
  );

  // ───────────────────── Load Delivery Location ──────────────────────

  const address = useDefaultDeliveryChannel({ orders });

  useEffectOnce(() => {
    if (isLoadingDeliveryLocation) return false;

    if (!isDelivery || !address) return false;

    changeDeliveryRegion(address);

    return true;
  }, [
    address,
    isDelivery,
    isFetchingCustomer,
    isLoadingOrders,
    isLoadingDeliveryLocation,
    setPlaceId,
    changeDeliveryRegion,
  ]);

  // ───────────────────── Sync Delivery Prediction ──────────────────────
  React.useEffect(() => {
    if (!googlePlacesAddress?.latitude || !isDelivery) return;

    changeDeliveryRegion(googlePlacesAddress);
  }, [googlePlacesAddress, isDelivery, changeDeliveryRegion]);

  // synchronize search strings in order to maintain consistent results for each search type
  React.useEffect(() => {
    setPredictionsSearchString(searchString);
  }, [searchString, setPredictionsSearchString]);

  // ─────────── going pickup/outpost from delivery runs query ───────────
  React.useEffect(() => {
    if (locationSearchType === 'delivery') return;

    if (!googlePlacesAddress.formattedAddress) return;

    setLocationQueryType('searchString');
    setSearchString(formatGoogleAddress(googlePlacesAddress) ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearchType]);

  // ─────────────────────────── Callbacks ───────────────────────────────
  const onOrderNow = React.useCallback(() => {
    const addresses = customer?.addresses ?? [];
    const existingAddress = addresses.find(
      ({ googlePlaceId }) =>
        googlePlaceId === googlePlacesAddress.googlePlaceId,
    );

    const existingAddressId = existingAddress?.id;
    const existingAddressName = existingAddress?.name ?? '';

    if (existingAddressId) {
      openDeliveryMenu(existingAddressId, existingAddressName);

      return;
    }

    void addAddress({
      input: {
        googlePlaceId: googlePlacesAddress.googlePlaceId ?? '',
        zipCode: googlePlacesAddress.zipCode ?? '',
        street: googlePlacesAddress.street ?? '',
        city: googlePlacesAddress.city ?? '',
        state: googlePlacesAddress.state ?? '',
        country: googlePlacesAddress.country ?? '',
        latitude: googlePlacesAddress.latitude ?? 0,
        longitude: googlePlacesAddress.longitude ?? 0,
        deliveryPreference: DeliveryPreferenceType.LeaveAtDoor,
        name: '',
        secondaryStreet: '',
        notes: '',
      },
    });
  }, [
    customer?.addresses,
    addAddress,
    googlePlacesAddress.googlePlaceId,
    googlePlacesAddress.zipCode,
    googlePlacesAddress.street,
    googlePlacesAddress.city,
    googlePlacesAddress.state,
    googlePlacesAddress.country,
    googlePlacesAddress.latitude,
    googlePlacesAddress.longitude,
    openDeliveryMenu,
  ]);
  const onResetDeliverySearch = React.useCallback(
    (clearSearch?: boolean) => {
      setPlaceId('');
      setDeliveryPin(null);
      setGooglePlacesAddress({});
      setLoadingAddress(false);
      setPredictionsData(defaultPredictions);

      if (clearSearch) setSearchString('');
    },
    [
      setGooglePlacesAddress,
      setLoadingAddress,
      setPredictionsData,
      setSearchString,
    ],
  );
  const onOrderPickup = React.useCallback(() => {
    setLocationSearchType('pickup');
  }, [setLocationSearchType]);

  // ─────────────────────────────────────────────────────────────────

  const onPredictionPress = React.useCallback(
    (selectedPlaceId: string) => {
      const hasSelectedActiveAddress =
        googlePlacesAddress?.googlePlaceId === selectedPlaceId;

      // update predictions search string if the user selects a prediction that matches active location
      if (hasSelectedActiveAddress)
        setPredictionsSearchString(formatGoogleAddress(googlePlacesAddress));

      setPlaceId(selectedPlaceId);
    },
    [googlePlacesAddress],
  );

  // ───────────────────────────── Response ─────────────────────────────────
  return {
    deliveryPin,
    isAddingAddress,
    googlePlacesAddress,
    foundLocation,
    fetchingDelivery,
    noLocationFound,
    predictionsData,
    predictionsSearchString,
    changeDeliveryRegion,
    setPredictionsData,
    setPredictionsSearchString,
    onPredictionPress,
    onOrderNow,
    onResetDeliverySearch,
    onOrderPickup,
  };
};

const defaultPredictions: LocationPredictionsData = {
  predictions: [],
  error: undefined,
  loading: false,
  noResults: false,
};
